import { useCallback, useEffect, useMemo, useState } from "react"
import { coreStore, APP_URL } from "@forcepoint/platform-coreui"
import { isEmpty } from "@forcepoint/platform-utilityui"
import CollapsedSideBar from "./CollapsedSideBar"
import ExpandedSideBar from "./ExpandedSideBar"
import { SidebarProps } from "./Sidebar.types"

export default function SideMenu({ isExpandedSideBar, setIsExpandedSideBar }: SidebarProps) {
  // ----------------------------------------------------------------------------------------
  // -------------------------------- Component States --------------------------------------
  // ----------------------------------------------------------------------------------------

  const getInitialActiveAppCode = () => {
    if (window.location.pathname.startsWith("/settings")) {
      return "settings"
    }
    if (window.location.pathname.startsWith("/home")) {
      return "home"
    }
    return coreStore.getState()?.activeApp?.applicationCode
  }

  const [activeAppCode, setActiveAppCode] = useState(getInitialActiveAppCode())

  const [openMenuCode, setOpenMenuCode] = useState(activeAppCode)

  const [hasTutorials, setHasTutorials] = useState(false)

  const sideBarOpenStatus = sessionStorage.getItem("isExpanded")

  useEffect(() => {
    const storeData = coreStore.getState()
    const userToken = storeData?.userToken
    if (!userToken) return

    const appCodes = (storeData?.applications || [])
      .filter((app: any) => app.showInSwitcher)
      .map((app: any) => app.applicationCode)
      .filter(Boolean)
      .join(",")
    const url = appCodes
      ? `${APP_URL}/api/product-updates?type=tutorial&limit=1&applicationCodes=${appCodes}`
      : `${APP_URL}/api/product-updates?type=tutorial&limit=1`

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${userToken}`
      }
    })
      .then((res) => res.json())
      .then((data) => {
        setHasTutorials(Array.isArray(data) && data.length > 0)
      })
      .catch(() => {
        setHasTutorials(false)
      })
  }, [])

  const { applications, platformServices, sideMenuData } = useMemo(() => {
    const applications = coreStore.getState()?.applications || []
    const settingsMenu = coreStore.getState()?.settingsMenu || []
    const showHomepage = coreStore.getState()?.showHomepage || false
    const sideMenuDataCopy = [
      ...applications.filter(
        (app) =>
          app.applicationCode.toLowerCase() !== "home" &&
          app.showInSwitcher &&
          ((app.isSPA && !isEmpty(app.menus)) ||
            app.applicationType === "external" ||
            app.openInIframe)
      ),
    ]
    if (settingsMenu?.length > 0) {
      sideMenuDataCopy.push({
        applicationName: "Settings",
        productLogo: "settings",
        applicationCode: "settings",
        menus: settingsMenu
      })
    }

    if (!window.location.hostname.includes("superadmin") && showHomepage) {
      const homeMenus: any[] = [
        {
          name: "Overview",
          localizationKey: "Overview",
          url: "/home",
          icon: "home",
          addOns: [],
          menus: null
        }
      ]
      if (hasTutorials) {
        homeMenus.push({
          name: "gettingStartedVideos",
          localizationKey: "Videos",
          url: "/home/product-tutorials",
          icon: "play",
          addOns: [],
          menus: null
        })
      }
      sideMenuDataCopy.unshift({
        applicationName: "Home",
        productLogo: "home",
        applicationCode: "HOME",
        applicationURL: "/home",
        menus: homeMenus,
        menuType: "application"
      })
    }

    const filteredApps = sideMenuDataCopy.reduce(
      (a, b) => {
        if (b.menuType !== "application") {
          a.core.push(b)
        } else {
          a.applications.push(b)
        }
        return a
      },
      { applications: [], core: [] }
    )
    return {
      applications: filteredApps.applications,
      platformServices: filteredApps.core,
      sideMenuData: sideMenuDataCopy
    }
  }, [coreStore.getState()?.showHomepage, hasTutorials])

  const onCollapseSideBar = useCallback(() => {
    setIsExpandedSideBar(false)
    sessionStorage.setItem("isExpanded", "false")
  }, [])

  useEffect(() => {
    if (sideBarOpenStatus === null) {
      sessionStorage.setItem("isExpanded", "true")
      setIsExpandedSideBar(true)
    }
  }, [])
  useEffect(() => {
    const coreStoreSubscription = coreStore.subscribe(() => {
      const stateData = coreStore.getState()
      let currentAppcode
      if (window.location.pathname.startsWith("/settings")) {
        currentAppcode = "settings"
      } else if (window.location.pathname.startsWith("/home")) {
        currentAppcode = "HOME"
      } else {
        currentAppcode = stateData?.activeApp?.applicationCode
      }
      setActiveAppCode(currentAppcode)
      setOpenMenuCode(currentAppcode)
    })
    return () => {
      coreStoreSubscription()
    }
  }, [])

  return (
    <div
      className={` ${isExpandedSideBar ? "main-sidemenu-container" : "collapsed-sidemenu-container"}`}
      style={{
        width: `${isExpandedSideBar ? "var(--platform-sidebar-width)" : "var(--platform-sidebar-width-collapsed)"}`
      }}
    >
      {isExpandedSideBar ? (
        <ExpandedSideBar
          applications={applications}
          platformServices={platformServices}
          setOpenMenuCode={setOpenMenuCode}
          openMenuCode={openMenuCode}
          activeAppCode={activeAppCode}
          onCollapseSideBar={onCollapseSideBar}
        />
      ) : (
        <CollapsedSideBar
          setIsExpandedSideBar={setIsExpandedSideBar}
          sideMenuData={sideMenuData}
          activeAppCode={activeAppCode}
        />
      )}
    </div>
  )
}
